/* eslint-disable no-underscore-dangle */
import React, { useState, useCallback, useLayoutEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { List, Box } from '@mui/material';
import { Drawer, Button, Icon } from '@fashionnovateam/nova-ui-toolkit';
import { HelpCenterNavigationProps } from '../types';
import { SidebarItem, SectionItem, ArticleItem } from './SideBarItems';

export function HelpCenterNavigation({
  sections,
  user,
}: HelpCenterNavigationProps) {
  const { pathname } = useLocation();
  const history = useHistory();
  const calculateIsMobile = () => window.innerWidth < 768;
  const [isMobile, setIsMobile] = useState(calculateIsMobile());
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  useLayoutEffect(() => {
    const handleResize = () => {
      setIsMobile(calculateIsMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSection = useCallback((sectionTitle: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionTitle)
        ? prev.filter((title) => title !== sectionTitle)
        : [...prev, sectionTitle],
    );
  }, []);

  const renderNavContent = () => (
    <Box sx={{ py: 2 }}>
      <List
        component="nav"
        sx={{
          '& .MuiListItemButton-root': {
            mx: 1,
          },
        }}
      >
        <Link
          to={`/users/${user.id}/help-center`}
          className="no-underline text-inherit"
        >
          <SidebarItem
            label="Introduction"
            icon="home/home-h-line"
            isActive={pathname === `/users/${user.id}/help-center`}
            onClick={() => setIsMobileMenuOpen(false)}
          />
        </Link>

        {sections?.map((section) => (
          <div key={section.title}>
            <SectionItem
              label={section.title}
              isActive={
                pathname ===
                `/users/${user.id}/help-center/${section.title.toLowerCase()}`
              }
              isExpanded={expandedSections.includes(section.title)}
              onToggle={() => toggleSection(section.title)}
              onNavigate={() => {
                toggleSection(section.title);
                history.push(
                  `/users/${user.id}/help-center/${section.title.toLowerCase()}`,
                );
              }}
              articles={section.articles}
            >
              {expandedSections.includes(section.title) && (
                <Box
                  sx={{
                    ml: 4,
                  }}
                >
                  {section.articles.map((article) => (
                    <Link
                      key={article._id}
                      to={`/users/${user.id}/help-center/${section.title.toLowerCase()}/${article._id}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <ArticleItem
                        label={article.title}
                        isActive={pathname.includes(article._id)}
                      />
                    </Link>
                  ))}
                </Box>
              )}
            </SectionItem>
          </div>
        ))}
      </List>
    </Box>
  );

  if (isMobile) {
    return (
      <>
        <div className="px-4 pt-4 w-full max-w-[800px]">
          <Button
            onClick={() => setIsMobileMenuOpen(true)}
            color="secondary"
            className="w-full py-2.5 px-4 flex items-center justify-center text-base font-normal"
          >
            Contents
            <Icon
              name="maximize/maximize-two-arrow"
              className="transform rotate-[-45deg]"
            />
          </Button>
        </div>

        <Drawer
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        >
          {renderNavContent()}
        </Drawer>
      </>
    );
  }

  return (
    <div className="hidden md:block w-[280px] flex-shrink-0 border-r-neutral-200 h-full overflow-auto">
      {renderNavContent()}
    </div>
  );
}
